import { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { Chip, Stack, type SxProps, Typography } from '@mui/material';
import { getModuleIdsFromGrants } from '@realadvisor/constants';
import { History } from 'lucide-react';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useRefreshMeAfterModuleGrant } from '@modules/users/hooks/useRefreshMeAfterModuleGrant';
import { CardAddMenu } from '@modules/users/user-overview/components/CardAddMenu';
import { UserModuleHistoryDialog } from '@modules/users/user-overview/components/UserModuleHistoryDialog';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { useExpandableCard } from '@modules/users/user-overview/hooks/useExpandableCard';
import { GET_USER, SET_USER_MODULE_GRANT } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import {
  CRM_MODULE_IDS_FOR_DISPLAY,
  getModuleLabel,
} from '@shared/utils/module-label';

const moduleChipSx: SxProps = {
  height: 22,
  fontSize: 12,
  '& .MuiChip-label': { px: 0.875 },
  '& .MuiChip-deleteIcon': { fontSize: 14, mr: '4px' },
};

type UserModulesCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

export const UserModulesCard = ({ user }: UserModulesCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { onModuleGrantCompleted, refreshError, resetRefreshError } =
    useRefreshMeAfterModuleGrant(user.id);
  const [historyOpen, setHistoryOpen] = useState(false);
  const isAdmin = me?.is_admin ?? false;
  const canEditModules = isAdmin && (user.is_broker || user.is_admin);
  const canViewSubscriptions = useCanViewSubscriptions(user.id);

  const { expand, accordionProps } = useExpandableCard();

  const modules = getModuleIdsFromGrants(user.module_grants);

  const [setModuleGrant, { error, reset }] = useMutation(
    SET_USER_MODULE_GRANT,
    {
      onCompleted: onModuleGrantCompleted,
      refetchQueries: [
        {
          query: GET_USER,
          variables: {
            id: user.id,
            includeSubscriptions: canViewSubscriptions,
          },
        },
      ],
      awaitRefetchQueries: true,
    },
  );

  const [pending, setPending] = useState<ReadonlyMap<string, 'add' | 'remove'>>(
    new Map(),
  );
  const setModules = (moduleId: string, kind: 'add' | 'remove') => {
    setPending(prev => new Map(prev).set(moduleId, kind));
    void setModuleGrant({
      variables: { id: user.id, module: moduleId, enabled: kind === 'add' },
    })
      .catch(() => {})
      .finally(() =>
        setPending(prev => {
          const nextPending = new Map(prev);
          nextPending.delete(moduleId);
          return nextPending;
        }),
      );
  };

  const displayedModules = [
    ...modules,
    ...[...pending]
      .filter(([id, kind]) => kind === 'add' && !modules.includes(id))
      .map(([id]) => id),
  ];

  const availableOptions = CRM_MODULE_IDS_FOR_DISPLAY.filter(
    moduleId => !modules.includes(moduleId) && pending.get(moduleId) !== 'add',
  ).map(moduleId => ({
    id: moduleId,
    label: getModuleLabel(moduleId, t),
    onClick: () => {
      expand();
      setModules(moduleId, 'add');
    },
  }));

  if (modules.length === 0 && !isAdmin) {
    return null;
  }

  return (
    <ListingAccordion
      title={t('Modules')}
      {...accordionProps}
      headerAction={
        isAdmin ? (
          <>
            <CardHeaderIconActions
              actions={[
                {
                  label: t('Module history'),
                  icon: <History size={16} />,
                  onClick: () => setHistoryOpen(true),
                },
              ]}
            />
            {canEditModules && (
              <CardAddMenu
                tooltip={t('Add module')}
                options={availableOptions}
              />
            )}
          </>
        ) : undefined
      }
    >
      {displayedModules.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {t('No modules assigned yet')}
        </Typography>
      ) : (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {displayedModules.map(m => (
            <Chip
              key={m}
              label={getModuleLabel(m, t)}
              size="small"
              variant="outlined"
              sx={moduleChipSx}
              onDelete={
                canEditModules ? () => setModules(m, 'remove') : undefined
              }
            />
          ))}
        </Stack>
      )}

      <MutationErrorSnackbar
        error={error ?? refreshError}
        onClose={() => {
          reset();
          resetRefreshError();
        }}
      />
      {isAdmin && (
        <UserModuleHistoryDialog
          key={user.id}
          open={historyOpen}
          userId={user.id}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </ListingAccordion>
  );
};
